/**
 * Homepage Starter Pack widget (sections/starter-pack-home.liquid).
 *
 * - Everything is rendered server side for both packs; this only switches between pre-rendered
 *   values and recalculates prices from the JSON in each pack (no price fetch).
 * - The inactive pack's images live in a <template> and are stamped in the first time it's chosen.
 * - Add to cart goes through window.StarterPackCart (assets/starter-pack-cart.js): the same cart
 *   lines as the Starter Pack PDP, tagged with source 'homepage_widget'. The theme cart opens the side cart.
 * - Any link to #starter-packs closes the mobile menu if it's open, scrolls here and moves focus
 *   to the section heading.
 *
 * Listeners on document are removed in disconnectedCallback, so theme editor section reloads
 * never leave duplicate listeners behind.
 */
if (!customElements.get('starter-pack-home')) {
  const SOURCE = 'homepage_widget';
  const HASH = '#starter-packs';

  // Same as the Starter Pack PDP: whole amounts drop the decimals (£23, £19.56)
  const formatMoney = (cents, format) => {
    const value = cents / 100;
    const amount = value % 1 === 0 ? value.toFixed(0) : value.toFixed(2);
    return (format || '£{{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      if (key === 'amount_no_decimals') return Math.round(value).toString();
      if (key === 'amount_with_comma_separator') return amount.replace('.', ',');
      return amount;
    });
  };

  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class StarterPackHome extends HTMLElement {
    connectedCallback() {
      if (this.dataset.bound === 'true') return;
      this.dataset.bound = 'true';
      this.text = JSON.parse(this.querySelector('[data-sph-text]').textContent);
      this.heading = this.querySelector('.sph__heading');
      this.packs = new Map();
      this.querySelectorAll('[data-pack-panel]').forEach((panel) => {
        const cfg = JSON.parse(panel.querySelector('[data-pack-config]').textContent);
        // Read the state from the inputs (browsers can restore form state on back/forward)
        const val = (sel, fallback) => { const el = panel.querySelector(sel); return el ? el.value : fallback; };
        const pack = {
          panel,
          cfg,
          state: {
            c1: Number(val('.sph-swatch__input[data-slot="1"]:checked', cfg.picks[0])),
            c2: Number(val('.sph-swatch__input[data-slot="2"]:checked', cfg.picks[1])),
            mode: val('[data-mode]:checked', 'once'),
            weeks: Number(val('[data-freq]', cfg.weeks)) || null,
            busy: false,
          },
        };
        this.packs.set(panel.dataset.packPanel, pack);
        this.render(pack);
      });
      const checked = this.querySelector('[data-pack-input]:checked');
      this.active = checked ? checked.value : this.dataset.defaultPack;

      this.onChange = this.handleChange.bind(this);
      this.onClick = this.handleClick.bind(this);
      this.onDocClick = this.handleDocClick.bind(this);
      this.addEventListener('change', this.onChange);
      this.addEventListener('click', this.onClick);
      document.addEventListener('click', this.onDocClick);

      this.updateMode();
      if (window.location.hash === HASH) this.focusHeading();
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('click', this.onClick);
      document.removeEventListener('click', this.onDocClick);
      delete this.dataset.bound;
    }

    get pack() {
      return this.packs.get(this.active);
    }

    /* ---------- Events ---------- */
    handleChange(e) {
      const t = e.target;
      if (t.matches('[data-pack-input]')) return this.selectPack(t.value);
      const pack = this.pack;
      if (!pack || !pack.panel.contains(t)) return;
      if (t.matches('.sph-swatch__input')) {
        const slot = t.dataset.slot;
        pack.state[`c${slot}`] = Number(t.value);
        this.render(pack);
        window.StarterPackCart && window.StarterPackCart.track('strength_select', { slot: Number(slot), strength: pack.cfg.coffees[Number(t.value)].name, source: SOURCE });
      } else if (t.matches('[data-mode]')) {
        pack.state.mode = t.value;
        this.render(pack);
        window.StarterPackCart && window.StarterPackCart.track('purchase_mode', { mode: t.value, source: SOURCE });
      } else if (t.matches('[data-freq]')) {
        pack.state.weeks = Number(t.value);
        this.render(pack);
        window.StarterPackCart && window.StarterPackCart.track('cadence_change', { weeks: pack.state.weeks, source: SOURCE });
      }
    }

    handleClick(e) {
      const thumb = e.target.closest('[data-media-thumb]');
      if (thumb) return this.showImage(thumb);
      const pair = e.target.closest('[data-pair]');
      if (pair) return this.selectPair(pair);
      const cta = e.target.closest('[data-cta]');
      if (cta) this.addToCart();
    }

    // Popular pairing pill: fill both slots (checking the radios too, so restored form state matches)
    selectPair(btn) {
      const pack = this.pack;
      if (!pack || !pack.panel.contains(btn)) return;
      pack.state.c1 = Number(btn.dataset.c1);
      pack.state.c2 = Number(btn.dataset.c2);
      [1, 2].forEach((slot) => {
        const input = pack.panel.querySelector(`.sph-swatch__input[data-slot="${slot}"][value="${pack.state[`c${slot}`]}"]`);
        if (input) input.checked = true;
      });
      this.render(pack);
      const { coffees } = pack.cfg;
      window.StarterPackCart && window.StarterPackCart.track('pairing_select', {
        pairing: btn.querySelector('.spp-pair__label').textContent.trim(),
        coffees: `${coffees[pack.state.c1].name} + ${coffees[pack.state.c2].name}`,
        source: SOURCE,
      });
    }

    // Links to #starter-packs: close the mobile menu if it's open, then jump and move focus.
    handleDocClick(e) {
      const link = e.target.closest(`a[href="${HASH}"]`);
      if (!link || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      e.preventDefault();
      const menu = document.querySelector('.mobile-menu-outer-wrapper');
      if (menu && menu.classList.contains('active') && menu.contains(link)) {
        // The theme's own close button (assets/menu.js toggles the menu on clicks inside #open-mobile-menu)
        const close = document.querySelector('#open-mobile-menu .closeMenu');
        if (close) close.click();
      }
      if (window.location.hash !== HASH) history.pushState(null, '', HASH);
      requestAnimationFrame(() => {
        this.querySelector('#starter-packs').scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' });
        this.focusHeading();
      });
    }

    focusHeading() {
      if (this.heading) this.heading.focus({ preventScroll: true });
    }

    /* ---------- Pack toggle ---------- */
    selectPack(key) {
      const next = this.packs.get(key);
      if (!next) return;
      const media = next.panel.querySelector('[data-media-template]');
      if (media) media.replaceWith(media.content.cloneNode(true));
      this.active = key;
      this.packs.forEach((pack, k) => { pack.panel.hidden = k !== key; });
      this.querySelectorAll('[data-pack-rating]').forEach((el) => { el.hidden = el.dataset.packRating !== key; });
      this.updateMode();
      window.StarterPackCart && window.StarterPackCart.track('starter_pack_select', { pack: next.cfg.packTitle, source: SOURCE });
    }

    updateMode() {
      const pack = this.pack;
      this.classList.toggle('is-once', !!pack && pack.state.mode !== 'sub');
    }

    showImage(thumb) {
      const media = thumb.closest('.sph-media');
      media.querySelectorAll('[data-media-index]').forEach((img) => { img.hidden = img.dataset.mediaIndex !== thumb.dataset.mediaThumb; });
      media.querySelectorAll('[data-media-thumb]').forEach((b) => {
        const on = b === thumb;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    }

    /* ---------- Prices + copy ---------- */
    planPrice(coffee, weeks) {
      const plan = window.StarterPackCart ? window.StarterPackCart.planFor(coffee, weeks) : null;
      return plan ? Number(plan.price) : Number(coffee.price || 0);
    }

    render(pack) {
      const { panel, cfg, state } = pack;
      const a = cfg.coffees[state.c1];
      const b = cfg.coffees[state.c2];
      if (!a || !b) return;
      const fmt = this.text.moneyFormat;
      const isSub = state.mode === 'sub' && state.weeks;
      const oncePrice = Number(a.price) + Number(b.price);
      const subPrice = isSub || state.weeks ? this.planPrice(a, state.weeks) + this.planPrice(b, state.weeks) : oncePrice;
      const set = (sel, value) => panel.querySelectorAll(sel).forEach((el) => { el.textContent = value; });

      set('[data-slot-name="1"]', a.label);
      set('[data-slot-name="2"]', b.label);
      panel.querySelectorAll('[data-pair]').forEach((btn) => {
        const p1 = Number(btn.dataset.c1);
        const p2 = Number(btn.dataset.c2);
        const on = (p1 === state.c1 && p2 === state.c2) || (p1 === state.c2 && p2 === state.c1);
        btn.classList.toggle('is-active', on);
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      set('[data-once-price]', formatMoney(oncePrice, fmt));
      set('[data-sub-price]', formatMoney(subPrice, fmt));
      set('[data-now-price]', formatMoney(isSub ? subPrice : oncePrice, fmt));
      const saving = Math.max(0, oncePrice - subPrice);
      const savingPct = oncePrice ? Math.round((saving / oncePrice) * 100) : 0;
      panel.querySelectorAll('[data-sub-was]').forEach((el) => { el.hidden = !(saving > 0); });
      panel.querySelectorAll('[data-sub-saving]').forEach((el) => {
        el.textContent = (this.text.saving || '').replace('[amount]', formatMoney(saving, fmt)).replace('[percent]', savingPct);
        el.hidden = !(saving > 0);
      });
      panel.querySelectorAll('[data-mode-card]').forEach((card) => card.classList.toggle('is-active', card.dataset.modeCard === (isSub ? 'sub' : 'once')));
      panel.querySelectorAll('[data-sub-details], [data-freq-wrap]').forEach((el) => { el.hidden = !isSub; });
      if (!state.busy) set('[data-cta-label]', isSub ? this.text.ctaSub : this.text.ctaOnce);
      set('[data-under-cta]', isSub ? this.text.underSub : this.text.underOnce);
      this.updateMode();
    }

    /* ---------- Cart ---------- */
    setStatus(pack, { error = '', success = '' }) {
      const err = pack.panel.querySelector('[data-error]');
      err.textContent = error;
      err.hidden = !error;
      pack.panel.querySelector('[data-success]').textContent = success;
    }

    setBusy(pack, busy) {
      pack.state.busy = busy;
      const cta = pack.panel.querySelector('[data-cta]');
      cta.disabled = busy;
      cta.setAttribute('aria-busy', busy ? 'true' : 'false');
      if (busy) pack.panel.querySelector('[data-cta-label]').textContent = this.text.adding;
      else this.render(pack);
    }

    async addToCart() {
      const pack = this.pack;
      if (!pack || pack.state.busy) return;
      const { cfg, state } = pack;
      const a = cfg.coffees[state.c1];
      const b = cfg.coffees[state.c2];
      if (!a || !b) return;
      this.setStatus(pack, {});
      this.setBusy(pack, true);
      try {
        if (!window.StarterPackCart) throw new Error('Starter Pack cart unavailable');
        const isSub = state.mode === 'sub' && state.weeks;
        await window.StarterPackCart.add({ cfg, coffees: [a, b], weeks: isSub ? state.weeks : null, source: SOURCE });
        const summary = `${cfg.packTitle} · ${a.name} + ${b.name}`;
        this.setStatus(pack, { success: (this.text.success || '').replace('[summary]', summary) });
      } catch (e) {
        console.error('[Starter Pack] add to cart failed', e);
        this.setStatus(pack, { error: this.text.error });
      } finally {
        this.setBusy(pack, false);
      }
    }
  }

  customElements.define('starter-pack-home', StarterPackHome);
}
